import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, ListGroup } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import api from '../services/api';

const Search = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (query) {
      performSearch();
    }
  }, [query]);

  const performSearch = async () => {
    try {
      const response = await api.get('/search/', { params: { q: query } });
      setResults(response.data);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Container>
    );
  }

  const totalResults = results ? 
    (results.news?.length || 0) + 
    (results.notices?.length || 0) + 
    (results.tenders?.length || 0) + 
    (results.services?.length || 0) : 0;

  return (
    <Container className="py-5">
      <h1 className="page-title mb-2">Search Results</h1>
      <p className="text-center text-muted mb-5">
        Found {totalResults} results for "{query}"
      </p>

      <Row>
        {results?.news && results.news.length > 0 && (
          <Col md={12} className="mb-4">
            <h3>News</h3>
            <ListGroup>
              {results.news.map(item => (
                <ListGroup.Item key={item.id}>
                  <h5>
                    <Link to={`/news/${item.slug}`}>{item.title}</Link>
                  </h5>
                  <p className="text-muted small mb-2">
                    {moment(item.published_date).format('MMMM DD, YYYY')}
                  </p>
                  <p className="mb-0">
                    {item.summary || item.content.replace(/<[^>]*>/g, '').substring(0, 200)}...
                  </p>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Col>
        )}

        {results?.notices && results.notices.length > 0 && (
          <Col md={12} className="mb-4">
            <h3>Notices</h3>
            <ListGroup>
              {results.notices.map(item => (
                <ListGroup.Item key={item.id}>
                  <h5>
                    <Link to={`/notices/${item.slug}`}>{item.title}</Link>
                  </h5>
                  <Badge bg="primary" className="me-2">{item.notice_type}</Badge>
                  <span className="text-muted small">
                    {moment(item.published_date).format('MMMM DD, YYYY')}
                  </span>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Col>
        )}

        {results?.tenders && results.tenders.length > 0 && (
          <Col md={12} className="mb-4">
            <h3>Tenders</h3>
            <ListGroup>
              {results.tenders.map(item => (
                <ListGroup.Item key={item.id}>
                  <h5>
                    <Link to={`/tenders/${item.slug}`}>{item.title}</Link>
                  </h5>
                  <p className="mb-0 text-muted small">
                    {item.tender_no} • Deadline: {moment(item.submission_deadline).format('MMM DD, YYYY')}
                  </p>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Col>
        )}

        {results?.services && results.services.length > 0 && (
          <Col md={12} className="mb-4">
            <h3>Services</h3>
            <Row>
              {results.services.map(item => (
                <Col key={item.id} md={6} className="mb-3">
                  <Card>
                    <Card.Body>
                      <Card.Title>
                        <Link to={`/services/${item.slug}`}>{item.name}</Link>
                      </Card.Title>
                      <Card.Text>
                        {item.description.replace(/<[^>]*>/g, '').substring(0, 100)}...
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>
        )}

        {totalResults === 0 && (
          <Col md={12}>
            <p className="text-center text-muted">No results found for your search query.</p>
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default Search;
